/**
 * IncidentsPage.tsx — /app/incidents — incident feed grouped by status
 *
 * Purpose:    Show open/acknowledged/resolved incidents with ack/resolve
 *             actions, manual incident creation (POST /v1/incidents), and a
 *             per-incident timeline (GET /v1/incidents/{id}/timeline).
 * Inputs:     incidentsApi from @/lib/saas-client
 * Outputs:    Grouped incident feed UI
 * Constraints: Dark-first styling; WCAG AA. Timeline route is rolling out —
 *              404/405/501 shows a quiet "coming online" line, never a crash.
 * SOT:        nsentry SaaS app zone; completion spec § G-WEB (incidents)
 */
import { useEffect, useState, type FormEvent } from 'react'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { incidentsApi, type Incident, type IncidentEvent } from '@/lib/saas-client'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

const SEVERITY_STYLES: Record<Incident['severity'], string> = {
  critical: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  major: 'bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
  minor: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
}

const STATUS_ORDER: Incident['status'][] = ['open', 'acknowledged', 'resolved']
const STATUS_LABELS: Record<Incident['status'], string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
}

function IncidentTimeline({ state }: { state: TimelineState }) {
  if (state.kind === 'loading') return <p className="text-xs text-brand-muted">Loading timeline…</p>
  if (state.kind === 'coming-online')
    return <p className="text-xs text-sky-700 dark:text-sky-400">Timeline history is coming online — events will appear here automatically.</p>
  if (state.kind === 'error') return <p className="text-xs text-yellow-700 dark:text-yellow-300">Timeline unavailable: {state.message}</p>
  if (state.events.length === 0) return <p className="text-xs text-brand-muted">No timeline events yet.</p>
  return (
    <ol className="space-y-1.5">
      {state.events.map((ev, i) => (
        <li key={`${ev.at}-${i}`} className="flex items-baseline gap-3 text-xs">
          <span className="flex-shrink-0 font-mono text-brand-muted">{new Date(ev.at).toLocaleString()}</span>
          <span className="rounded-full bg-brand-surface-2 px-2 py-0.5 font-medium text-brand-secondary">{ev.kind}</span>
          <span className="text-brand-muted">{ev.message}</span>
        </li>
      ))}
    </ol>
  )
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return `${Math.round(diffH / 24)}d ago`
}

type TimelineState =
  | { kind: 'loading' }
  | { kind: 'coming-online' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; events: IncidentEvent[] }

export function IncidentsPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [error, setError] = useState<SentryError | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<Incident['severity']>('minor')
  const [formError, setFormError] = useState<string | null>(null)
  const [timelines, setTimelines] = useState<Record<string, TimelineState>>({})

  async function load() {
    setState('loading')
    const result = await incidentsApi.list()
    if (!result.ok) {
      setError(result.error)
      setState('error')
      return
    }
    setIncidents(result.value)
    setState(result.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  async function handleAck(id: string) {
    await incidentsApi.ack(id)
    await load()
  }

  async function handleResolve(id: string) {
    await incidentsApi.resolve(id)
    await load()
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    const result = await incidentsApi.create({ title, severity })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setShowForm(false)
    setTitle('')
    setSeverity('minor')
    await load()
  }

  async function toggleTimeline(id: string) {
    if (timelines[id]) {
      setTimelines((t) => {
        const next = { ...t }
        delete next[id]
        return next
      })
      return
    }
    setTimelines((t) => ({ ...t, [id]: { kind: 'loading' } }))
    const result = await incidentsApi.timeline(id)
    setTimelines((t) => ({
      ...t,
      [id]: result.ok
        ? { kind: 'ready', events: result.value }
        : isComingOnline(result.error)
          ? { kind: 'coming-online' }
          : { kind: 'error', message: result.error.message },
    }))
  }

  const grouped = STATUS_ORDER.map((status) => ({
    status,
    items: incidents.filter((i) => i.status === status),
  })).filter((g) => g.items.length > 0)

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-brand-ink">Incidents</h1>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
        >
          New incident
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Create incident">
          {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="inc-title" className="mb-1 block text-sm font-medium text-brand-secondary">Title</label>
              <input id="inc-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Elevated API latency" className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none" />
            </div>
            <div>
              <label htmlFor="inc-severity" className="mb-1 block text-sm font-medium text-brand-secondary">Severity</label>
              <select id="inc-severity" value={severity} onChange={(e) => setSeverity(e.target.value as Incident['severity'])} className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none">
                <option value="minor">minor</option>
                <option value="major">major</option>
                <option value="critical">critical</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">Open incident</button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2">Cancel</button>
          </div>
        </form>
      )}

      <AsyncScreen state={state} error={error} onRetry={load} emptyMessage="No incidents — all systems healthy">
        <div className="space-y-8">
          {grouped.map(({ status, items }) => (
            <section key={status} aria-label={STATUS_LABELS[status]}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">
                {STATUS_LABELS[status]} ({items.length})
              </h2>
              <ul className="space-y-3">
                {items.map((incident) => (
                  <li key={incident.id} className="rounded-xl border border-brand-subtle bg-brand-surface p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLES[incident.severity]}`}>
                          {incident.severity}
                        </span>
                        <div>
                          <p className="font-medium text-brand-ink">{incident.title}</p>
                          <p className="text-xs text-brand-muted">{relativeTime(incident.startedAt)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => toggleTimeline(incident.id)}
                          aria-expanded={Boolean(timelines[incident.id])}
                          className="rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
                        >
                          Timeline
                        </button>
                        {incident.status === 'open' && (
                          <button
                            type="button"
                            onClick={() => handleAck(incident.id)}
                            className="rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
                          >
                            Acknowledge
                          </button>
                        )}
                        {incident.status !== 'resolved' && (
                          <button
                            type="button"
                            onClick={() => handleResolve(incident.id)}
                            className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                    {timelines[incident.id] && (
                      <div className="mt-3 border-t border-brand-subtle pt-3">
                        <IncidentTimeline state={timelines[incident.id]} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </AsyncScreen>
    </div>
  )
}
